import { useState, useRef } from "react";
import { KeyResult } from "@/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { KeyResultForm } from "./KeyResultForm";

interface KeyResultDialogsProps {
  isFormOpen: boolean;
  onFormOpenChange: (open: boolean) => void;
  onKeyResultSaved?: (keyResult: KeyResult) => void;
  editingKeyResult: KeyResult | null;
  parentObjectivePid?: string;
  isDeleteOpen: boolean;
  onDeleteOpenChange: (open: boolean) => void;
  keyResultToDelete: KeyResult | null;
  onConfirmDelete: () => void;
  isDeleting: boolean;
}

export function KeyResultDialogs({
  isFormOpen,
  onFormOpenChange,
  onKeyResultSaved,
  editingKeyResult,
  parentObjectivePid,
  isDeleteOpen,
  onDeleteOpenChange,
  keyResultToDelete,
  onConfirmDelete,
  isDeleting,
}: KeyResultDialogsProps) {
  const submitRef = useRef<(() => void) | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <>
      <Dialog open={isFormOpen} onOpenChange={onFormOpenChange}>
        <DialogContent className="sm:max-w-[50vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingKeyResult ? "Edit Key Result" : "Add Key Result"}</DialogTitle>
          </DialogHeader>
          <KeyResultForm
            key={editingKeyResult?.pid || parentObjectivePid || "new"}
            initialData={editingKeyResult || undefined}
            parentObjectivePid={parentObjectivePid}
            onSuccess={(keyResult) => {
              onFormOpenChange(false);
              onKeyResultSaved?.(keyResult);
            }}
            hideButtons
            onSubmitRef={(fn) => { submitRef.current = fn; }}
            onSubmittingChange={setIsSubmitting}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => onFormOpenChange(false)}>Cancel</Button>
            <Button onClick={() => submitRef.current?.()} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingKeyResult ? "Save Changes" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteOpen} onOpenChange={onDeleteOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Key Result</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{keyResultToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmDelete} disabled={isDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
